import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { guildsApi } from '../api/guilds';

interface ServerSelectorProps {
  selectedGuildId: string | null;
  onSelectGuild: (guildId: string) => void;
}

export default function ServerSelector({ selectedGuildId, onSelectGuild }: ServerSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { data: guilds, isLoading, error } = useQuery({
    queryKey: ['guilds'],
    queryFn: guildsApi.getGuilds,
  });

  const selectedGuild = guilds?.find((g) => g.id === selectedGuildId);

  // 첫 번째 길드를 자동 선택
  useEffect(() => {
    if (guilds && guilds.length > 0 && !selectedGuildId) {
      onSelectGuild(guilds[0].id);
    }
  }, [guilds, selectedGuildId, onSelectGuild]);

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2 px-4 py-2 bg-white rounded-lg border border-gray-200">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
        <span className="text-sm text-gray-600">서버 로딩 중...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-2 bg-red-50 rounded-lg border border-red-200">
        <span className="text-sm text-red-600">서버 목록을 불러올 수 없습니다</span>
      </div>
    );
  }

  if (!guilds || guilds.length === 0) {
    return (
      <div className="px-4 py-2 bg-yellow-50 rounded-lg border border-yellow-200">
        <span className="text-sm text-yellow-700">관리 가능한 서버가 없습니다</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-4 py-2 bg-white rounded-lg border border-gray-300 hover:border-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Select server"
      >
        <div className="flex items-center space-x-3">
          {selectedGuild?.icon ? (
            <img
              src={`https://cdn.discordapp.com/icons/${selectedGuild.id}/${selectedGuild.icon}.png`}
              alt=""
              className="w-8 h-8 rounded-full"
              aria-hidden="true"
            />
          ) : (
            <div 
              className="w-8 h-8 rounded-full bg-gradient-to-r from-[#FF7A7A] to-[#DD8DFF] flex items-center justify-center text-white font-bold"
              aria-hidden="true"
            >
              {selectedGuild?.name.charAt(0).toUpperCase()}
            </div>
          )}
          <span className="font-medium text-gray-900">{selectedGuild?.name || '서버 선택'}</span>
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <ul 
            className="absolute z-20 w-full mt-2 bg-white rounded-lg border border-gray-200 shadow-lg max-h-60 overflow-y-auto"
            role="listbox"
            aria-label="Server list"
          >
            {guilds.map((guild) => (
              <li key={guild.id} role="option" aria-selected={guild.id === selectedGuildId}>
                <button
                  onClick={() => {
                    onSelectGuild(guild.id);
                    setIsOpen(false);
                  }}
                  className={`flex items-center space-x-3 w-full px-4 py-3 hover:bg-gray-50 transition-colors focus:outline-none focus:bg-gray-100 ${
                    guild.id === selectedGuildId ? 'bg-gray-50' : ''
                  }`}
                  aria-label={`Select ${guild.name}`}
                >
                  {guild.icon ? (
                    <img
                      src={`https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png`}
                      alt=""
                      className="w-8 h-8 rounded-full"
                      aria-hidden="true"
                    />
                  ) : (
                    <div 
                      className="w-8 h-8 rounded-full bg-gradient-to-r from-[#FF7A7A] to-[#DD8DFF] flex items-center justify-center text-white font-bold"
                      aria-hidden="true"
                    >
                      {guild.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="font-medium text-gray-900">{guild.name}</span>
                  {guild.id === selectedGuildId && (
                    <svg 
                      className="w-5 h-5 text-green-500 ml-auto" 
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                      aria-label="Selected"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
